import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard, Bot, Megaphone, PhoneCall,
  BarChart3, BookOpen, Settings, LogOut, Phone, Menu, X, PlugZap, Hash, ShoppingBag, UserPlus,
} from 'lucide-react';
import { useState } from 'react';
import { Button } from './ui/button';

const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { path: '/dashboard/agents', icon: Bot, label: 'AI Agents' },
  { path: '/dashboard/campaigns', icon: Megaphone, label: 'Campaigns' },
  { path: '/dashboard/calls', icon: PhoneCall, label: 'Call History' },
  { path: '/dashboard/oncall-leads', icon: UserPlus, label: 'On-Call Leads' },
  { path: '/dashboard/analytics', icon: BarChart3, label: 'Analytics' },
  { path: '/dashboard/phone-numbers', icon: Hash, label: 'Phone Numbers' },
  { path: '/dashboard/ecommerce', icon: ShoppingBag, label: 'E-Commerce' },
  { path: '/dashboard/integrations', icon: PlugZap, label: 'Integrations' },
  { path: '/dashboard/knowledge-base', icon: BookOpen, label: 'Knowledge Base' },
  { path: '/dashboard/settings', icon: Settings, label: 'Settings' },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const SidebarContent = () => (
    <>
      <div className="p-5 border-b border-white/5">
        <div className="flex items-center gap-3">
          <img src="https://i.ibb.co/ZRpFcYDK/oie-s-XZ9-BUTHM5-Vz.png" alt="DotCall" className="h-8 w-auto" />
        </div>
      </div>

      <nav className="flex-1 py-4 px-3 space-y-1" data-testid="sidebar-nav">
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.end}
            onClick={() => setSidebarOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                isActive
                  ? 'bg-amber-600/15 text-amber-300 border-r-2 border-amber-500'
                  : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`
            }
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <item.icon className="w-4 h-4 flex-shrink-0" />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="p-4 border-t border-white/5">
        <div className="flex items-center gap-3 px-2 mb-3">
          <div className="w-8 h-8 rounded-full bg-amber-600/20 flex items-center justify-center text-amber-300 text-xs font-semibold">
            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm text-white truncate">{user?.name || 'User'}</div>
            <div className="text-xs text-slate-500 truncate">{user?.email}</div>
          </div>
        </div>
        <Button
          variant="ghost"
          onClick={handleLogout}
          data-testid="logout-button"
          className="w-full justify-start text-slate-400 hover:text-red-400 hover:bg-red-500/10 text-sm h-9"
        >
          <LogOut className="w-4 h-4 mr-2" /> Sign Out
        </Button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: '#020617' }} data-testid="dashboard-layout">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:flex-col w-60 border-r border-white/5 bg-[#0B1221]/80 backdrop-blur-xl flex-shrink-0">
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setSidebarOpen(false)} />
          <aside className="relative w-60 h-full flex flex-col bg-[#0B1221] border-r border-white/5">
            <button onClick={() => setSidebarOpen(false)} className="absolute top-4 right-4 text-slate-400">
              <X className="w-5 h-5" />
            </button>
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile Header */}
        <header className="lg:hidden flex items-center gap-3 p-4 border-b border-white/5 bg-[#0B1221]/80 backdrop-blur-xl">
          <button onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-button">
            <Menu className="w-5 h-5 text-slate-300" />
          </button>
          <img src="https://i.ibb.co/ZRpFcYDK/oie-s-XZ9-BUTHM5-Vz.png" alt="DotCall" className="h-7 w-auto" />
        </header>

        <main className="flex-1 overflow-y-auto p-6 lg:p-8" data-testid="main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
